<template>
  <div class="prompting">
    <!-- Header -->
    <div class="monaco-card" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0; border-left: none; border-right: none; border-top: none; background: linear-gradient(135deg, #007ACC, #005A9E); color: white;">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold mb-4" style="text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);">
            🤖 AI PROMPTING CENTER 🤖
          </h1>
          <p class="text-xl md:text-2xl mb-4 opacity-90">
            Chat with Real AI Models - GPT-4, Mistral, Azure OpenAI & More!
          </p>
          <div class="flex justify-center items-center space-x-4 text-sm">
            <span class="px-3 py-1 bg-white/20 rounded-full">✨ Real AI</span>
            <span class="px-3 py-1 bg-white/20 rounded-full">🚀 Fast Responses</span>
            <span class="px-3 py-1 bg-white/20 rounded-full">💬 Multiple Models</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Sidebar -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" style="border: 2px solid #007ACC; box-shadow: 0 0 20px rgba(0, 122, 204, 0.2);">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              🤖 <span class="ml-2">CHOOSE AI MODEL</span>
            </h2>
            <select 
              v-model="selectedModel" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium"
              style="border: 2px solid #007ACC; background: #F0F8FF;"
            >
              <option value="gpt-4">🧠 GPT-4 (Most Powerful)</option>
              <option value="gpt-3.5">⚡ GPT-3.5 Turbo (Fast)</option>
              <option value="mistral">🔥 Mistral AI (Professional)</option>
              <option value="claude-3">🎭 Claude 3 (Creative)</option>
              <option value="asi1-mini">💎 ASI:One Mini (Efficient)</option>
              <option value="gemini-pro">🌟 Gemini Pro (Advanced)</option>
            </select>
            <div class="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p class="text-sm text-blue-800 font-medium">
                💡 <strong>Tip:</strong> GPT-4 for complex tasks, Mistral for translations, ASI:One for quick responses!
              </p>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" style="border: 2px solid #28A745; box-shadow: 0 0 20px rgba(40, 167, 69, 0.2);">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              🎯 <span class="ml-2">SELECT TASK TYPE</span>
            </h2>
            <div class="space-y-2">
              <label
                v-for="task in taskTypes"
                :key="task.id"
                class="flex items-center space-x-2 cursor-pointer hover:bg-green-50 p-3 rounded border-2 border-transparent hover:border-green-300 transition-all"
                style="border-radius: 8px;"
              >
                <input
                  type="radio"
                  :value="task.id"
                  v-model="selectedTask"
                  class="text-green-600 focus:ring-green-500 w-5 h-5"
                />
                <div class="flex-1">
                  <div class="font-medium text-gray-900 text-lg">{{ task.name }}</div>
                  <div class="text-sm text-gray-600">{{ task.description }}</div>
                </div>
              </label>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style="border: 2px solid #FFC107; box-shadow: 0 0 20px rgba(255, 193, 7, 0.2);">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              ⚡ <span class="ml-2">QUICK START TEMPLATES</span>
            </h2>
            <div class="space-y-2">
              <button
                v-for="template in templates"
                :key="template.id"
                @click="useTemplate(template)"
                class="w-full text-left px-3 py-3 text-sm bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-all duration-200 border-2 border-transparent hover:border-yellow-300 transform hover:scale-105"
                style="border-radius: 8px;"
              >
                <div class="font-medium text-gray-900 text-base">{{ template.name }}</div>
                <div class="text-xs text-gray-600">{{ template.description }}</div>
              </button>
            </div>
            <div class="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p class="text-sm text-yellow-800 font-medium">
                🚀 <strong>Quick Tip:</strong> Click any template to instantly fill the prompt area!
              </p>
            </div>
          </div>
        </div>

        <!-- Main Prompt Area -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" style="border: 3px solid #007ACC; box-shadow: 0 0 30px rgba(0, 122, 204, 0.3);">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-2xl font-bold text-gray-900 flex items-center">
                💬 <span class="ml-2">TYPE YOUR PROMPT</span>
              </h2>
              <div class="flex items-center space-x-2">
                <button
                  @click="clearPrompt"
                  class="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 border-2 border-gray-300 rounded-lg hover:border-gray-500"
                >
                  🗑️ Clear
                </button>
                <button
                  @click="savePrompt"
                  class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 border-2 border-blue-600 hover:border-blue-700"
                >
                  💾 Save
                </button>
              </div>
            </div>

            <textarea
              v-model="prompt"
              placeholder="🤖 Type your question or request here... (e.g., 'Explain quantum physics in simple terms' or 'Write a Python function to sort an array')"
              class="w-full h-40 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 resize-none text-lg"
              style="background: #F0F8FF; border-color: #007ACC;"
            ></textarea>

            <div class="mt-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-6">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="settings.includeContext"
                      class="text-blue-600 focus:ring-blue-500 w-5 h-5"
                    />
                    <span class="text-sm text-gray-700 font-medium">📚 Include context</span>
                  </label>
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="settings.creativeMode"
                      class="text-blue-600 focus:ring-blue-500 w-5 h-5"
                    />
                    <span class="text-sm text-gray-700 font-medium">🎨 Creative mode</span>
                  </label>
                </div>
                <div class="flex items-center space-x-3">
                  <select
                    v-model="settings.temperature"
                    class="px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  >
                    <option value="0.1">🎯 Precise</option>
                    <option value="0.5">⚖️ Balanced</option>
                    <option value="0.8">🎨 Creative</option>
                    <option value="1.0">🎲 Random</option>
                  </select>
                  <button
                    @click="generateResponse"
                    :disabled="!prompt.trim() || isGenerating"
                    class="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-lg font-bold transform hover:scale-105 border-2 border-blue-600"
                    style="box-shadow: 0 4px 15px rgba(0, 122, 204, 0.3);"
                  >
                    {{ isGenerating ? '🤖 Thinking...' : '🚀 GENERATE RESPONSE' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Response Area -->
          <div v-if="response || isGenerating" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style="border: 3px solid #28A745; box-shadow: 0 0 30px rgba(40, 167, 69, 0.3);">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-2xl font-bold text-gray-900 flex items-center">
                ✨ <span class="ml-2">AI RESPONSE</span>
              </h2>
              <div class="flex items-center space-x-2">
                <button
                  v-if="response"
                  @click="copyResponse"
                  class="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 border-2 border-gray-300 rounded-lg hover:border-gray-500"
                >
                  📋 Copy
                </button>
                <button
                  v-if="response"
                  @click="regenerateResponse"
                  :disabled="isGenerating"
                  class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 border-2 border-green-600 hover:border-green-700"
                >
                  🔄 Regenerate
                </button>
              </div>
            </div>

            <div v-if="isGenerating" class="flex items-center justify-center py-12">
              <div class="animate-spin rounded-full h-12 w-12 border-b-4 border-green-600"></div>
              <span class="ml-4 text-xl text-gray-700 font-medium">🤖 AI is thinking...</span>
            </div>

            <div v-else-if="response" class="prose max-w-none">
              <div class="whitespace-pre-wrap text-gray-800 text-lg leading-relaxed p-6 bg-green-50 rounded-lg border-2 border-green-200">
                {{ response }}
              </div>
            </div>
          </div>

          <!-- History -->
          <div v-if="history.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style="border: 2px solid #6C757D; box-shadow: 0 0 20px rgba(108, 117, 125, 0.2);">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-xl font-bold text-gray-900 flex items-center">
                📚 <span class="ml-2">CONVERSATION HISTORY</span>
              </h2>
              <button
                @click="clearHistory"
                class="px-4 py-2 text-sm text-red-600 hover:text-red-900 transition-colors duration-200 border-2 border-red-300 rounded-lg hover:border-red-500"
              >
                🗑️ Clear History
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(item, index) in history"
                :key="index"
                class="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-lg"
              >
                <div class="flex justify-between items-start mb-2">
                  <div class="flex-1">
                    <div class="text-sm font-bold text-gray-900 flex items-center">
                      👤 <span class="ml-2">You:</span>
                    </div>
                    <div class="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">{{ item.prompt }}</div>
                  </div>
                  <div class="text-xs text-gray-500 font-medium">{{ formatTime(item.timestamp) }}</div>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-bold text-gray-900 flex items-center">
                    🤖 <span class="ml-2">AI ({{ item.model }}):</span>
                  </div>
                  <div class="text-sm text-gray-700 bg-blue-50 p-2 rounded border border-blue-200 whitespace-pre-wrap">{{ item.response }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const selectedModel = ref('gpt-4')
const selectedTask = ref('general')
const prompt = ref('')
const response = ref('')
const isGenerating = ref(false)
const history = ref([])

const settings = ref({
  includeContext: true,
  creativeMode: false,
  temperature: '0.7'
})

const taskTypes = ref([
  { id: 'general', name: 'General', description: 'General purpose assistance' },
  { id: 'writing', name: 'Writing', description: 'Creative writing and editing' },
  { id: 'coding', name: 'Coding', description: 'Code generation and debugging' },
  { id: 'analysis', name: 'Analysis', description: 'Data analysis and insights' },
  { id: 'translation', name: 'Translation', description: 'Language translation' },
  { id: 'summarization', name: 'Summarization', description: 'Text summarization' }
])

const templates = ref([
  {
    id: 'blog-post',
    name: 'Blog Post',
    description: 'Generate a blog post outline',
    prompt: 'Create a blog post outline about [topic] with an engaging introduction, 3-4 main points, and a conclusion.'
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Professional email template',
    prompt: 'Write a professional email to [recipient] about [subject] with a clear call to action.'
  },
  {
    id: 'code-review',
    name: 'Code Review',
    description: 'Review code for improvements',
    prompt: 'Review this code and suggest improvements for performance, readability, and best practices:\n\n[code here]'
  },
  {
    id: 'story',
    name: 'Story',
    description: 'Creative story prompt',
    prompt: 'Write a short story about [character] who [situation] in [setting].'
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Marketing copy',
    prompt: 'Create compelling marketing copy for [product/service] targeting [audience].'
  },
  {
    id: 'learning',
    name: 'Learning',
    description: 'Explain concept',
    prompt: 'Explain [concept] in simple terms for someone who is new to this topic.'
  }
])

const generateResponse = async () => {
  if (!prompt.value.trim()) return
  
  isGenerating.value = true
  
  try {
    let aiResponse = ''
    
    // Try different AI services based on selected model
    if (selectedModel.value === 'mistral' || selectedModel.value === 'claude-3') {
      aiResponse = await callMistralAPI()
    } else if (selectedModel.value === 'gpt-4' || selectedModel.value === 'gpt-3.5') {
      aiResponse = await callAzureOpenAI()
    } else if (selectedModel.value === 'gemini-pro') {
      aiResponse = await callASIOneAPI()
    } else {
      aiResponse = await callMistralAPI() // Default fallback
    }
    
    response.value = aiResponse
    
    // Add to history
    history.value.unshift({
      prompt: prompt.value,
      response: response.value,
      model: selectedModel.value,
      task: selectedTask.value,
      timestamp: new Date()
    })
    
  } catch (error) {
    console.error('AI API Error:', error)
    response.value = `Sorry, I encountered an error: ${error.message}. Please try again.`
  } finally {
    isGenerating.value = false
  }
}

const callMistralAPI = async () => {
  const apiKey = import.meta.env.VITE_MISTRAL_API_KEY
  const response = await fetch('https://api.mistral.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: 'mistral-small',
      messages: [
        {
          role: 'system',
          content: getSystemPrompt()
        },
        {
          role: 'user',
          content: prompt.value
        }
      ],
      max_tokens: 1000,
      temperature: parseFloat(settings.value.temperature)
    })
  })
  
  if (!response.ok) {
    throw new Error(`Mistral API error: ${response.status}`)
  }
  
  const data = await response.json()
  return data.choices[0].message.content
}

const callAzureOpenAI = async () => {
  const apiKey = import.meta.env.VITE_AZURE_OPENAI_API_KEY
  const endpoint = import.meta.env.VITE_AZURE_OPENAI_ENDPOINT
  const deployment = import.meta.env.VITE_AZURE_OPENAI_IMAGE_DEPLOYMENT || 'gpt-4'
  
  const response = await fetch(`${endpoint}/openai/deployments/${deployment}/chat/completions?api-version=2024-02-15-preview`, {
    method: 'POST',
    headers: {
      'api-key': apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages: [
        {
          role: 'system',
          content: getSystemPrompt()
        },
        {
          role: 'user',
          content: prompt.value
        }
      ],
      max_tokens: 1000,
      temperature: parseFloat(settings.value.temperature)
    })
  })
  
  if (!response.ok) {
    throw new Error(`Azure OpenAI error: ${response.status}`)
  }
  
  const data = await response.json()
  return data.choices[0].message.content
}

const callASIOneAPI = async () => {
  const apiKey = import.meta.env.VITE_ASI_ONE_API_KEY
  const response = await fetch('https://api.asi1.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: 'asi1-mini',
      messages: [
        {
          role: 'system',
          content: getSystemPrompt()
        },
        {
          role: 'user',
          content: prompt.value
        }
      ],
      max_tokens: 1000,
      temperature: parseFloat(settings.value.temperature)
    })
  })
  
  if (!response.ok) {
    throw new Error(`ASI:One API error: ${response.status}`)
  }
  
  const data = await response.json()
  return data.choices[0].message.content
}

const getSystemPrompt = () => {
  const prompts = {
    general: 'You are a helpful AI assistant. Provide clear, accurate, and helpful responses.',
    writing: 'You are a professional writer. Create well-structured, engaging, and grammatically correct content.',
    coding: 'You are an expert programmer. Provide clean, efficient, and well-commented code solutions.',
    analysis: 'You are a data analyst. Provide insightful analysis with clear conclusions and actionable recommendations.',
    translation: 'You are a professional translator. Provide accurate translations that preserve meaning and cultural context.',
    summarization: 'You are an expert at summarization. Create concise summaries that capture the most important information.'
  }
  
  return prompts[selectedTask.value] || prompts.general
}

const regenerateResponse = async () => {
  await generateResponse()
}

const copyResponse = () => {
  navigator.clipboard.writeText(response.value)
  // You could add a toast notification here
}

const clearPrompt = () => {
  prompt.value = ''
  response.value = ''
}

const savePrompt = () => {
  // Save to localStorage or backend
  const savedPrompts = JSON.parse(localStorage.getItem('savedPrompts') || '[]')
  savedPrompts.push({
    prompt: prompt.value,
    model: selectedModel.value,
    task: selectedTask.value,
    settings: { ...settings.value },
    timestamp: new Date()
  })
  localStorage.setItem('savedPrompts', JSON.stringify(savedPrompts))
  // You could add a success notification here
}

const useTemplate = (template) => {
  prompt.value = template.prompt
}

const clearHistory = () => {
  history.value = []
}

const formatTime = (date) => {
  return new Date(date).toLocaleString()
}
</script>

<style scoped>
.prompting {
  min-height: calc(100vh - 200px);
}

/* Custom animations */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Prose styling for AI responses */
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose p {
  margin-bottom: 1em;
}

.prose ul, .prose ol {
  margin-bottom: 1em;
  padding-left: 1.5em;
}

.prose li {
  margin-bottom: 0.5em;
}

.prose code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}

.prose pre {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
}

.prose pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}
</style>
